/**
 * Where each thing on a contact's profile goes when it is clicked.
 *
 * A profile is a set of handles, not a set of labels: an address is a message
 * waiting to be written, a deal id is a dossier, a message subject is the
 * message. These are the handles, in one place, so the side panel and the
 * full window open the same things the same way.
 */
import { useDealDossierStore } from '@/store/dealDossierStore'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** `mailto:` for an address, or null when the value is not one. */
export function mailtoHref(email: string | null | undefined): string | null {
  const text = String(email ?? '').trim()
  if (!EMAIL_RE.test(text)) return null
  return `mailto:${text}`
}

/** `tel:` for a number, or null when there are not enough digits to dial. */
export function telHref(phone: string | null | undefined): string | null {
  const text = String(phone ?? '').trim()
  const digits = text.replace(/[^\d+]/g, '')
  if (digits.replace(/\D/g, '').length < 7) return null
  return `tel:${digits}`
}

/**
 * An absolute URL for a website or LinkedIn value, or null for junk.
 *
 * Signatures write "www.estreich.com" and "linkedin.com/in/pat"; a browser
 * needs the scheme. A value with no dot in it, or with spaces, is not a site.
 */
export function externalHref(url: string | null | undefined): string | null {
  const text = String(url ?? '').trim()
  if (!text || /\s/.test(text)) return null
  const withScheme = /^https?:\/\//i.test(text) ? text : `https://${text}`
  try {
    const parsed = new URL(withScheme)
    if (!parsed.hostname.includes('.')) return null
    return parsed.toString()
  } catch {
    return null
  }
}

/** A map search for an address. */
export function mapsHref(address: string | null | undefined): string | null {
  const text = String(address ?? '').trim()
  if (!text) return null
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`
}

/** The intake record a message is. There is no message route of its own;
 *  `/intake?focus=` is what global search emits and what Intake honours. */
export function emailHref(ingestionId: string | null | undefined): string | null {
  const text = String(ingestionId ?? '').trim()
  return text ? `/intake?focus=${encodeURIComponent(text)}` : null
}

/** Open a deal's dossier in place. The host is mounted once in the shell, so
 *  this works from any page without navigating. */
export function openDeal(dealId: string, tab: 'overview' | 'parties' | 'documents' | 'journal' = 'overview'): void {
  if (!dealId) return
  useDealDossierStore.getState().openDossier(dealId, tab)
}

/** A hostname to show for a link, without the scheme and path noise. */
export function linkLabel(url: string | null | undefined): string {
  const href = externalHref(url)
  if (!href) return String(url ?? '')
  try {
    const parsed = new URL(href)
    const path = parsed.pathname.replace(/\/$/, '')
    return `${parsed.hostname.replace(/^www\./, '')}${path && path !== '/' ? path : ''}`
  } catch {
    return String(url ?? '')
  }
}
